import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import arrow_left from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import edit from "../assets/images/navbar/edit.png";
import { useRestaurant } from "../context/RestaurantContext";
import { kitchenService } from "../services";

type OutletTimingProps = {
  setIsOutletTimingOpen: (open: boolean) => void;
};

type TimeSlot = {
  openTime: string;
  closeTime: string;
};

type DaySchedule = {
  day: string;
  code: string;
  isOpen: boolean;
  slots: TimeSlot[];
};

const DAYS: { day: string; code: string }[] = [
  { day: "Monday", code: "MON" },
  { day: "Tuesday", code: "TUE" },
  { day: "Wednesday", code: "WED" },
  { day: "Thursday", code: "THU" },
  { day: "Friday", code: "FRI" },
  { day: "Saturday", code: "SAT" },
  { day: "Sunday", code: "SUN" },
];

const toTimeInput = (value?: string) => {
  if (!value) return "10:00";
  const match24 = value.match(/^(\d{2}:\d{2})/);
  if (match24) return match24[1];
  const parsed = Date.parse(`1970-01-01T${value}`);
  if (!Number.isNaN(parsed)) return new Date(parsed).toISOString().slice(11, 16);
  return "10:00";
};

const OutletTiming = ({ setIsOutletTimingOpen }: OutletTimingProps) => {
  const { restaurantProfile, refreshProfile } = useRestaurant();
  const [expandedDay, setExpandedDay] = useState<number | null>(0);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<DaySchedule[]>(
    DAYS.map((item) => ({
      ...item,
      isOpen: true,
      slots: [{ openTime: "10:00", closeTime: "23:00" }],
    }))
  );

  useEffect(() => {
    if (!restaurantProfile) return;
    const operatingDays: string[] = restaurantProfile.operatingDays || [];
    const openTime = toTimeInput(
      restaurantProfile.operatingHours?.openTime || restaurantProfile.openTime
    );
    const closeTime = toTimeInput(
      restaurantProfile.operatingHours?.closeTime || restaurantProfile.closeTime
    );
    setSchedule(
      DAYS.map((item) => ({
        ...item,
        isOpen: operatingDays.length ? operatingDays.includes(item.code) : true,
        slots: [{ openTime, closeTime }],
      }))
    );
  }, [restaurantProfile]);

  const toggleDay = (index: number) => {
    const next = [...schedule];
    next[index].isOpen = !next[index].isOpen;
    setSchedule(next);
  };

  const updateSlot = (
    dayIndex: number,
    slotIndex: number,
    field: "openTime" | "closeTime",
    value: string
  ) => {
    const next = [...schedule];
    next[dayIndex].slots[slotIndex][field] = value;
    setSchedule(next);
  };

  const handleSave = async () => {
    const openDays = schedule.filter((day) => day.isOpen);
    if (openDays.length === 0) {
      toast.error("Select at least one operating day");
      return;
    }
    const firstSlot = openDays[0].slots[0];
    setSaving(true);
    try {
      const response: any = await kitchenService.submitStep4({
        operatingDays: openDays.map((day) => day.code),
        openTime: firstSlot.openTime,
        closeTime: firstSlot.closeTime,
        avgPreparationTime: restaurantProfile?.avgPreparationTime || 30,
        acceptsPreOrders: restaurantProfile?.acceptsPreOrders || false,
        maxOrdersPerHour: restaurantProfile?.maxOrdersPerHour || 10,
      });
      toast.success(response.message || "Outlet timings saved");
      await refreshProfile();
      setIsOutletTimingOpen(false);
    } catch (error: any) {
      toast.error(error?.message || "Failed to save outlet timings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsOutletTimingOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 rounded-l-[20px] overflow-y-auto flex flex-col"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full p-6 flex-1">
          <div className="w-full flex justify-between items-center mb-[24px]">
            <div className="flex items-center gap-3">
              <img
                src={arrow_left}
                alt="back"
                className="w-[36px] h-[36px] rounded-full border cursor-pointer p-2"
                onClick={() => setIsOutletTimingOpen(false)}
              />
              <h2 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                Outlet Timings
              </h2>
              <img src={edit} alt="edit" className="h-[24px] w-[24px] dark:invert" />
            </div>
            <img
              src={cancel}
              alt="close"
              className="w-[36px] h-[36px] p-2 rounded-full border cursor-pointer"
              onClick={() => setIsOutletTimingOpen(false)}
            />
          </div>

          <div className="w-full space-y-2">
            {schedule.map((item, index) => (
              <div
                key={item.code}
                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-[#F5F5F5] dark:bg-gray-800"
              >
                <div className="flex items-center justify-between p-4">
                  <button
                    onClick={() => setExpandedDay(expandedDay === index ? null : index)}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <span className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium">
                      {item.day}
                    </span>
                  </button>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={item.isOpen}
                      onChange={() => toggleDay(index)}
                    />
                    <div className={`relative w-16 h-8 rounded-full ${item.isOpen ? "bg-[#FF8A00]" : "bg-gray-300"}`}>
                      <div className={`absolute top-1 bg-white w-6 h-6 rounded-full ${item.isOpen ? "left-9" : "left-1"}`}></div>
                    </div>
                  </label>
                </div>
                {expandedDay === index && item.isOpen && (
                  <div className="px-4 pb-4 space-y-3">
                    {item.slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="flex items-center gap-3">
                        <input
                          type="time"
                          value={slot.openTime}
                          onChange={(e) => updateSlot(index, slotIndex, "openTime", e.target.value)}
                          className="flex-1 px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 dark:text-gray-200"
                        />
                        <input
                          type="time"
                          value={slot.closeTime}
                          onChange={(e) => updateSlot(index, slotIndex, "closeTime", e.target.value)}
                          className="flex-1 px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 dark:text-gray-200"
                        />
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">
                      Hours are saved as your kitchen operating hours for all open days.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 pt-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 text-white font-semibold disabled:opacity-50"
            style={{ borderRadius: 150, background: "#F30" }}
          >
            {saving ? "Saving..." : "Save timings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutletTiming;
